import React, { useEffect, useRef } from 'react';
import { Viewport } from '../WorldCanvas/WorldCanvas';

/** Euclidean distance from origin beyond which zones are free. Must match backend ZoneFreeRadiusMin. */
export const ZONE_FREE_RADIUS = 3000;

interface FreeBoundaryOverlayProps {
  viewport : Viewport;
  width    : number;
  height   : number;
}

/**
 * Draws the free-zone boundary on a canvas layer.
 *
 * - Area OUTSIDE the circle (radius 20 000 from world origin) is tinted green → free.
 * - Area INSIDE the circle is slightly darkened → paid.
 * - The boundary circle itself has a dashed green stroke + label.
 *
 * Screen ↔ world (Y-up, same as WorldCanvas):
 *   screenX =  (worldX − vp.x) × zoom + w/2
 *   screenY = −(worldY − vp.y) × zoom + h/2
 */
export const FreeBoundaryOverlay: React.FC<FreeBoundaryOverlayProps> = ({
  viewport, width, height,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    const { x: vpX, y: vpY, zoom } = viewport;

    // World origin on screen
    const ox =  (0 - vpX) * zoom + width  / 2;
    const oy = -(0 - vpY) * zoom + height / 2;

    // Radius in screen pixels
    const r = ZONE_FREE_RADIUS * zoom;

    // If the circle is too small to matter, skip
    if (r < 2) return;

    // ── Fill paid area (inside circle) — very subtle dark tint ────────
    ctx.save();
    ctx.beginPath();
    ctx.arc(ox, oy, r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
    ctx.fill();
    ctx.restore();

    // ── Fill free area (outside circle, clipped to viewport) ─────────
    ctx.save();
    ctx.beginPath();
    // Full viewport rect, then cut out the circle
    ctx.rect(0, 0, width, height);
    ctx.arc(ox, oy, r, 0, Math.PI * 2, true); // clockwise = hole
    ctx.fillStyle = 'rgba(80, 220, 100, 0.07)';
    ctx.fill('evenodd');
    ctx.restore();

    // ── Boundary circle stroke ─────────────────────────────────────────
    ctx.save();
    ctx.beginPath();
    ctx.arc(ox, oy, r, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(80, 220, 100, 0.65)';
    ctx.lineWidth   = Math.max(1.5, zoom * 0.05);
    ctx.setLineDash([Math.max(6, zoom * 0.5), Math.max(4, zoom * 0.3)]);
    ctx.stroke();
    ctx.restore();

    // ── Label "FREE ZONE →" near the top of the circle ────────────────
    const labelAngle = -Math.PI / 2; // top of circle
    const lx = ox + Math.cos(labelAngle) * r;
    const ly = oy + Math.sin(labelAngle) * r;

    // Only draw label if the circle top is within viewport
    if (lx >= 0 && lx <= width && ly >= -20 && ly <= height + 20) {
      const fontSize = Math.max(10, Math.min(14, zoom * 0.8));
      ctx.font         = `${fontSize}px 'Courier New', monospace`;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'bottom';

      const label = '⟵ FREE ZONE';
      const lxLabel = lx;
      const lyLabel = ly - 6;

      ctx.fillStyle = 'rgba(0,0,0,0.65)';
      ctx.fillText(label, lxLabel + 1, lyLabel + 1);
      ctx.fillStyle = 'rgba(80, 220, 100, 0.95)';
      ctx.fillText(label, lxLabel, lyLabel);
    }

    // ── Origin crosshair (small, faint) ───────────────────────────────
    if (zoom >= 0.5) {
      const cs = Math.max(4, Math.min(12, zoom * 0.8));
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth   = 1;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(ox - cs, oy); ctx.lineTo(ox + cs, oy);
      ctx.moveTo(ox, oy - cs); ctx.lineTo(ox, oy + cs);
      ctx.stroke();
      ctx.restore();
    }
  }, [viewport, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position     : 'absolute',
        top          : 0,
        left         : 0,
        pointerEvents: 'none',
        zIndex       : 3,
      }}
    />
  );
};
